import React, { useEffect, useMemo, useRef, useState } from 'react';

const OTHER = 'Other';

const counselorLabel = (c) => {
  if (!c) return '';
  return c.counselor_id ? `${c.name} (${c.counselor_id})` : (c.name || '');
};

const parseSubmittedBy = (value, counselors = []) => {
  const raw = String(value || '').trim();
  if (!raw) return { mode: '', otherText: '', selectedId: '' };

  if (raw === OTHER || raw.startsWith('Other:')) {
    return {
      mode: OTHER,
      otherText: raw.startsWith('Other:') ? raw.replace(/^Other:\s*/, '') : '',
      selectedId: '',
    };
  }

  const byName = counselors.find(
    (c) => String(c.name || '').trim().toLowerCase() === raw.toLowerCase()
  );
  if (byName) {
    return { mode: 'counselor', otherText: '', selectedId: String(byName.id) };
  }

  // Legacy free-text values that aren't a known counselor
  return { mode: OTHER, otherText: raw, selectedId: '' };
};

export default function CounselorSearchSelect({
  counselors = [],
  value = '',
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Search counselor...',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const parsed = useMemo(
    () => parseSubmittedBy(value, counselors),
    [value, counselors]
  );

  const selectedCounselor = counselors.find((c) => String(c.id) === parsed.selectedId);

  useEffect(() => {
    if (parsed.mode === OTHER) {
      setQuery(OTHER);
    } else if (selectedCounselor) {
      setQuery(counselorLabel(selectedCounselor));
    } else if (!value) {
      setQuery('');
    }
  }, [parsed.mode, selectedCounselor, value]);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const term = query.toLowerCase();
  const filtered = counselors.filter((c) => {
    if (parsed.mode === OTHER && query === OTHER) return true;
    const name = (c.name || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const cid = (c.counselor_id || '').toLowerCase();
    return name.includes(term) || email.includes(term) || cid.includes(term);
  });

  const showOtherInList = !term || OTHER.toLowerCase().includes(term) || term === 'other';

  const pickCounselor = (c) => {
    onChange(c.name || '');
    setQuery(counselorLabel(c));
    setOpen(false);
  };

  const pickOther = () => {
    const existingOther = parsed.mode === OTHER ? parsed.otherText : '';
    onChange(existingOther ? `Other: ${existingOther}` : OTHER);
    setQuery(OTHER);
    setOpen(false);
  };

  const handleOtherText = (text) => {
    const trimmed = text.trim();
    onChange(trimmed ? `Other: ${trimmed}` : OTHER);
  };

  return (
    <div ref={wrapRef}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          className="input-field"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange('');
          }}
          onFocus={() => !disabled && setOpen(true)}
          autoComplete="off"
          required={required && !value}
        />
        {open && !disabled && (
          <div style={{
            position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
            maxHeight: '260px', overflowY: 'auto', boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
            marginTop: '4px',
          }}>
            {filtered.length === 0 && !showOtherInList ? (
              <div style={{ padding: '12px 14px', color: '#6b7280', fontSize: '13px' }}>
                No counselors found
              </div>
            ) : (
              <>
                {showOtherInList && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickOther();
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', border: 'none',
                      borderBottom: filtered.length ? '1px solid #f3f4f6' : 'none',
                      background: parsed.mode === OTHER ? '#fff7ed' : 'white',
                      padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    }}
                  >
                    Other
                  </button>
                )}
                {filtered.slice(0, 40).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickCounselor(c);
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', border: 'none',
                      background: String(c.id) === parsed.selectedId ? '#fff7ed' : 'white',
                      padding: '10px 14px', cursor: 'pointer', fontSize: '13px',
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{counselorLabel(c)}</div>
                    {c.email && <div style={{ color: '#6b7280', fontSize: '12px' }}>{c.email}</div>}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {parsed.mode === OTHER && (
        <input
          type="text"
          className="input-field"
          style={{ marginTop: '8px', marginBottom: 0 }}
          placeholder="Enter name"
          value={parsed.otherText}
          disabled={disabled}
          onChange={(e) => handleOtherText(e.target.value)}
          required={required}
        />
      )}
    </div>
  );
}
